import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { messages } from '../i18n/messages.ts';
import { sound } from '../lib/sound.ts';
import { readStorage, writeStorage } from '../lib/storage.ts';
import { switchLocale, useUrlLocale } from './router.ts';
import {
  PREFERENCES_STORAGE_KEY,
  PreferencesContext,
  restorePreferences,
  type Preferences,
  type Translate,
} from './preferences.ts';

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<Preferences>(() =>
    restorePreferences(readStorage<Partial<Preferences>>(PREFERENCES_STORAGE_KEY, {})),
  );

  // The language always follows the URL (/ = Turkish, /en = English) so every
  // page has one stable address per language for search engines.
  const locale = useUrlLocale();

  const update = useCallback(
    (patch: Partial<Preferences>) => {
      if (patch.locale && patch.locale !== locale) switchLocale(patch.locale);
      setPrefs((prev) => ({ ...prev, ...patch }));
    },
    [locale],
  );

  useEffect(() => {
    writeStorage(PREFERENCES_STORAGE_KEY, { ...prefs, locale });
    sound.setEnabled(prefs.sound);
    document.documentElement.lang = locale;
  }, [prefs, locale]);

  // Resolve light/dark, following the OS when set to "system".
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const dark = prefs.theme === 'dark' || (prefs.theme === 'system' && media.matches);
      document.documentElement.dataset.theme = dark ? 'dark' : 'light';
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#121a36' : '#e6f3ff');
    };
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [prefs.theme]);

  const t = useCallback<Translate>(
    (key, vars) => {
      let text: string = messages[locale][key] ?? messages.en[key] ?? key;
      if (vars) {
        for (const [name, value] of Object.entries(vars)) text = text.replaceAll(`{${name}}`, String(value));
      }
      return text;
    },
    [locale],
  );

  const value = useMemo(() => ({ prefs: { ...prefs, locale }, update, t }), [prefs, locale, update, t]);
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}
